'use client';

// The live Inbox count's bridge (BL-2491), the twin of the change queue's
// (ChangeQueueCountBridge, BL-2438). The count's snapshot is subscribed up here,
// inside the realtime stack (RealtimeProviderMount), but its topic is minted
// inside the signed-in shell, which knows the person and the house. The shell's
// reporter mints the topic and reports it up; the sidebar reads the count from
// here, docked on desktop and in the phone's drawer (BUG-2093 removed the phone
// bar's icon).
//
// The count reaches the screen only over its snapshot (rule #32): the mint
// carries a topic, never a number.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createMintedTopicsHook, useSnapshotState, type StaffUnreadsSnapshot } from '@auctogon/core/realtime';
import { isOfflineBlocked } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';

import { api } from '../../api-client/typed-client';
import { useAuth } from '../auth/AuthProvider';
import { useActiveTenantId } from '../tenant/TenantProvider';
import { useOfflineMode } from '../offline/OfflineModeProvider';

/** The Inbox's route, as the offline rule names it. */
const INBOX_PATH = '/inbox';

/** What a reporter hands the bridge. */
interface ReportedTopic {
  /** The count's topic, or `''` for no subscription. */
  topic: string;
  /** Whether the topic could not be minted. */
  mintFailed: boolean;
}

/** The unread Inbox count as the badges read it. */
export interface InboxUnreadCount {
  /** Unread rows; 0 while unknown. */
  count: number;
  /** True when the count could not be loaded and none has landed. */
  unavailable: boolean;
  /**
   * Bumps each time a new count snapshot lands, even one that leaves the number
   * equal (BUG-2260). The open Inbox page re-reads its list on it: the snapshot is
   * the trigger, never a source for the rows (rule #32, option (b)).
   */
  revision: number;
}

const NO_TOPIC: ReportedTopic = { topic: '', mintFailed: false };

const ReportInboxTopicContext = createContext<((reported: ReportedTopic) => void) | null>(null);
const InboxUnreadCountContext = createContext<InboxUnreadCount | null>(null);

/**
 * Subscribes to the reported Inbox count topic for the surfaces above the signed-in shell.
 *
 * @param props.children - the app.
 * @returns the provider.
 * @sideEffect Subscribes to the reported topic and runs its catch-up.
 */
export function InboxCountBridge({ children }: { children: ReactNode }) {
  const [reported, setReported] = useState<ReportedTopic>(NO_TOPIC);
  const { snapshot, error } = useSnapshotState<StaffUnreadsSnapshot>(reported.topic);
  // Keep the state's identity when nothing changed, so a re-report costs no render.
  const report = useCallback((next: ReportedTopic) => {
    setReported((prev) => (prev.topic === next.topic && prev.mintFailed === next.mintFailed ? prev : next));
  }, []);
  // Unknown only when it failed AND nothing ever landed: a later failure on top of a
  // real count leaves that count standing, as the bidder's bell does (BL-1787).
  const unavailable = (error != null || reported.mintFailed) && snapshot == null;
  // `??`, never `||`: 0 is a real count.
  const count = snapshot?.unreadCount ?? 0;
  // The store replaces the snapshot object on every applied update, so a new
  // identity is a new version even when the count did not change.
  const [seen, setSeen] = useState<{ snapshot: StaffUnreadsSnapshot | undefined; revision: number }>({
    snapshot: undefined,
    revision: 0,
  });
  if (snapshot !== seen.snapshot) setSeen({ snapshot, revision: seen.revision + (snapshot != null ? 1 : 0) });
  const revision = seen.revision;
  const value = useMemo<InboxUnreadCount>(() => ({ count, unavailable, revision }), [count, unavailable, revision]);
  return (
    <ReportInboxTopicContext.Provider value={report}>
      <InboxUnreadCountContext.Provider value={value}>{children}</InboxUnreadCountContext.Provider>
    </ReportInboxTopicContext.Provider>
  );
}

/**
 * @returns the unread Inbox count, or null with no bridge mounted (a shell rendered alone).
 */
export function useInboxUnreadCount(): InboxUnreadCount | null {
  return useContext(InboxUnreadCountContext);
}

/**
 * Mint the signed-in person's private topics at one house, once per
 * `${staffUserId}:${tenantId}` key: the count's snapshot and (BL-2473) the mention
 * pop-ups' ledger. The route derives both from the session and the gated house;
 * the key only says which house to ask about. The count's reporter and the
 * pop-up listener share this cache, so a house costs one mint.
 *
 * @sideEffect `GET /api/realtime/staff-topics?tenantId=` on the first ask per key.
 */
export const useStaffTopics = createMintedTopicsHook<{ unreadsTopic: string; toastsTopic: string }>({
  load: async (key) => {
    const tenantId = key.slice(key.lastIndexOf(':') + 1);
    const body = await api.realtime.staff_topics.get({ query: { tenantId } });
    if (!body.success) throw new Error(body.error);
    return { unreadsTopic: body.data.unreadsTopic, toastsTopic: body.data.toastsTopic };
  },
  empty: { unreadsTopic: '', toastsTopic: '' },
});

/**
 * The mint key for the signed-in person at the active house, or `''` while no
 * mint may run: signed out, between houses, before the platform is known (the
 * phone app may be about to say it is offline, and a mint then would be a
 * request the offline mode forbids, ADR 066 — Offline Mode § 6.2), or while the
 * Inbox needs a connection the phone app doesn't have.
 *
 * @returns `${staffUserId}:${tenantId}`, or `''`.
 */
export function useStaffTopicsKey(): string {
  const { user } = useAuth();
  const tenantId = useActiveTenantId();
  const { isOffline, nativeDetected } = useOfflineMode();
  const userId = user?.id ?? '';
  const canMint = userId !== '' && tenantId != null && nativeDetected && !isOfflineBlocked(INBOX_PATH, isOffline);
  return canMint ? `${userId}:${tenantId}` : '';
}

/**
 * Mint the count's topic for the signed-in person at the active house and report
 * it to the bridge; reports no topic when signed out, between houses, before the
 * platform is known, while the Inbox needs a connection the phone app doesn't have,
 * or with no bridge mounted,
 * and clears it on unmount, so no surface keeps another session's or house's count.
 *
 * @sideEffect Mints the topic (see `useStaffTopics`) and writes the bridge's topic.
 */
export function useReportInboxTopic(): void {
  const report = useContext(ReportInboxTopicContext);
  const key = useStaffTopicsKey();
  const { unreadsTopic, error } = useStaffTopics(report != null ? key : '');
  const mintFailed = error != null;

  useEffect(() => {
    report?.({ topic: unreadsTopic, mintFailed });
  }, [report, unreadsTopic, mintFailed]);
  useEffect(() => () => report?.(NO_TOPIC), [report]);
}

/**
 * Mounts {@link useReportInboxTopic} once inside the signed-in shell.
 *
 * @returns nothing visible.
 */
export function InboxTopicReporter() {
  useReportInboxTopic();
  return null;
}
